import { api } from "../lib/api";
import { sendToTerminal } from "../lib/terminalLogger";

// ============================================================
// Tipos — espelho do backend ResolvedPath
// ============================================================
export type ResolvedPath = {
	smb: {
		directory: string;
		fileName: string;
		fullPath: string;
	};
	drive: {
		segments: string[];
		fileName: string;
		relativePath: string;
	};
	metadata: {
		flow: string;
		baseDir: string;
		storageMethod: "smb" | "gdrive";
		isUrgente: boolean;
		isNoturna: boolean;
		dateTarget: string;
		regraId?: number;
	};
};

// ============================================================
// Payloads por fluxo
// ============================================================
export type OrderSavePayload = {
	flow: "order_save";
	orc_id: number;
	oit_id: number;
	role: string;
	qtdObjetos?: number;
	contraction?: string;
	delivery?: string;
};

export type SaveTempPayload = {
	flow: "save_temp";
	baseName: string;
	serviceType: string;
	quality: string;
	totalObjectsWithQuantity?: number;
	contraction?: string;
	hasMultipleContractions?: boolean;
	entrega?: string;
	categoriaId: number;
};

export type PreOrcamentoPayload = {
	flow: "pre_orcamento";
	userId: number;
	cliWhatsapp: string;
	hasCadastro: boolean;
	nomeOpcional?: string;
	clienteId?: number;
	clienteNomeFantasia?: string;
	categoriaId?: number;
	categoriaNome?: string;
};

export type ExportPayload = {
	flow: "export";
	orc_id: number;
	oit_id: number;
	index?: number;
	multiplicador?: number;
	contraction?: string;
	exportType?: "png" | "production";
};

export type PathResolvePayload =
	| OrderSavePayload
	| SaveTempPayload
	| PreOrcamentoPayload
	| ExportPayload;

// ============================================================
// API
// ============================================================

/**
 * Chamada única ao POST /path/resolve.
 */
async function callResolve(payload: PathResolvePayload): Promise<ResolvedPath> {
	const res = await api("/path/resolve", {
		method: "POST",
		body: JSON.stringify(payload),
	});

	if (!res.ok) {
		throw new Error(res.message || res.code || "Erro ao resolver path");
	}

	// Backend retorna { ok: true, result: ResolvedPath }
	return res.result || res.data;
}

/**
 * Resolve path via backend com retry automático.
 * - Tenta até `maxRetries` vezes (padrão 2 = 1 tentativa + 1 retry).
 * - Delay entre tentativas: `delayMs` (padrão 1500ms).
 * - Lança o último erro se todas as tentativas falharem.
 */
export async function resolveFilePath(
	payload: PathResolvePayload,
	{
		maxRetries = 2,
		delayMs = 1500,
	}: { maxRetries?: number; delayMs?: number } = {},
): Promise<ResolvedPath> {
	const startTime = performance.now();

	sendToTerminal("📂 [PATH] Resolvendo path via backend", {
		flow: payload.flow,
	});

	let lastError: unknown;

	for (let attempt = 1; attempt <= maxRetries; attempt++) {
		try {
			const resolved = await callResolve(payload);
			const durationMs = Math.round(performance.now() - startTime);

			sendToTerminal("✅ [PATH] Path resolvido", {
				storageMethod: resolved.metadata.storageMethod,
				fileName: resolved.smb.fileName,
				attempt,
				durationMs,
			});

			return resolved;
		} catch (error: any) {
			lastError = error;
			const message = error instanceof Error ? error.message : String(error);
			const status = error?.status || error?.httpStatus || "unknown";

			sendToTerminal(`⚠️ [PATH] Tentativa ${attempt}/${maxRetries} falhou`, {
				flow: payload.flow,
				error: message,
				status,
			});

			// Se ainda tem tentativas, aguardar antes de retryar
			if (attempt < maxRetries) {
				await new Promise((r) => setTimeout(r, delayMs));
			}
		}
	}

	// Todas as tentativas falharam
	const durationMs = Math.round(performance.now() - startTime);
	const message =
		lastError instanceof Error
			? (lastError as Error).message
			: String(lastError);

	sendToTerminal("❌ [PATH] Todas as tentativas falharam", {
		flow: payload.flow,
		error: message,
		totalAttempts: maxRetries,
		durationMs,
	});

	throw lastError;
}
